<script lang="ts">
	// The design system's Meter (design/components/surfaces/Meter.jsx): its
	// only chart, a bar of a value from 0 to 100 on a track.
	//
	// Two speeds. An ordinary change (another period, another shop) moves at
	// --dur-enter on the flat curve. `settled` is for the render where the
	// value resolves, and takes the needle's curve; a meter that settles on
	// every update settles on nothing.
	//
	// `dim` quiets a bar beside a stronger one, so the peak of a set reads at
	// a glance.

	interface Props {
		value: number;
		tone?: 'ledger' | 'bright' | 'pale' | 'flag' | 'speed';
		height?: number;
		dim?: boolean;
		settled?: boolean;
		label?: string;
		testId?: string;
	}

	let {
		value,
		tone = 'ledger',
		height = 9,
		dim = false,
		settled = false,
		label,
		testId,
	}: Props = $props();

	const pct = $derived(Math.max(0, Math.min(100, value)));
</script>

<span
	class="meter"
	role="progressbar"
	aria-valuenow={Math.round(pct)}
	aria-valuemin={0}
	aria-valuemax={100}
	aria-label={label}
	data-testid={testId}
	style:block-size="{height}px"
>
	<span class="fill {tone}" class:dim class:settled style:inline-size="{pct}%"></span>
</span>

<style>
	.meter {
		display: block;
		min-inline-size: 0;
		background: var(--surface-track);
		border-radius: var(--radius-pill);
		overflow: hidden;
	}

	.fill {
		display: block;
		block-size: 100%;
		border-radius: var(--radius-pill);
		transition: inline-size var(--dur-enter) var(--ease-out);
	}

	.fill.settled {
		transition: inline-size var(--dur-settle) var(--ease-settle);
	}

	.fill.dim {
		opacity: 0.72;
	}

	.ledger {
		background: var(--ledger);
	}

	.bright {
		background: var(--signal-ledger);
	}

	.pale {
		background: var(--ledger-bright);
	}

	.flag {
		background: var(--signal-flag);
	}

	.speed {
		background: var(--signal-speed);
	}
</style>
